import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowBack } from '@mui/icons-material';
import { CircularProgress, Divider } from '@mui/material';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { upperFirst } from 'lodash';

import ItemWithSwitch from '@/operator/pages/connections-page/ItemWithSwitch';
import { StoreDataConnectorConfigInput } from '@/operator/services/graphql/mutations/storeDataConnectorConfig';
import { ConnectToCRMAPIInput } from '@/operator/services/graphql/types/ConnectToCRMAPIInput';
import { toUnixTime } from '@/operator/utils/utils';
import { Step3ToggleRadioGroup } from '@/operator/components/general/cards/Step4ToggleForm';
import ErrorStep from '../../general/multiStepForm/Error';
import { PageStateContext } from '../../general/multiStepForm/formStateContext';
import {
    StyledDivButtons,
    StyledDivConfig,
    StyledDivDescription,
    StyledDivInputs,
    StyledLink,
} from './Styles';
import {
    ConnectionSystem,
    useCreateConnectionMutation,
} from 'generated/graphql';
import { selectPartnerData } from 'store/partner/partnerSelector';
import { useAppSelector } from 'hooks/index';

const labelZendeskLink =
    'https://support.zendesk.com/hc/en-us/articles/4408835056154-Using-labels-on-your-help-center-articles#:~:text=Labels%20are%20a%20single%20word,related%20articles%20based%20on%20labels.';

const colorTypographyGray = 'rgba(0, 0, 0, 0.60)';

interface FormSelectDataProps {
    readonly onContinue: () => void;
    readonly onCancel: () => void;
    readonly onBack: () => void;
    readonly refreshOrganizationData: () => void;
    readonly connectionData?: ConnectToCRMAPIInput;
}
function FormSelectData({
    onContinue,
    onCancel,
    onBack,
    refreshOrganizationData,
    connectionData,
}: Readonly<FormSelectDataProps>) {
    const { t } = useTranslation();

    const [storingConnectorConfig, setStoringConnectorConfig] = useState(false);
    const { operatorConnectionEnableTalk, operatorConnectionEnableHelp } =
        useFlags();

    const pageState = useContext(PageStateContext);

    const { instanceId } = useAppSelector(selectPartnerData);

    const [createConnection, { data, error, reset }] =
        useCreateConnectionMutation();

    const onSuccessfulStoreDataConnector = () => {
        onContinue();
        refreshOrganizationData();
    };

    useEffect(() => {
        if (data?.createConnection.active) {
            onSuccessfulStoreDataConnector();
        }
        setStoringConnectorConfig(false);
    }, [data?.createConnection.active]);

    const [selectedRadioButton, setSelectedRadioButton] =
        useState<string>('radiobutton-all');

    const radioList = [
        {
            id: 'radiobutton-all',
            label: t(
                'Forms.Connections.Step3.ToggleSteps.Toggle4.RadioOptions.Option1Label',
            ),
        },
        {
            id: 'radiobutton-last3',
            label: t(
                'Forms.Connections.Step3.ToggleSteps.Toggle4.RadioOptions.Option2Label',
            ),
        },
        {
            id: 'radiobutton-from-today',
            label: t(
                'Forms.Connections.Step3.ToggleSteps.Toggle4.RadioOptions.Option3Label',
            ),
        },
    ];

    const getExistingDataTimestamp = () => {
        let existingData = 0;

        if (selectedRadioButton === 'radiobutton-from-today') {
            const currentDate = new Date();
            existingData = toUnixTime(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                currentDate.getDate(),
                0,
                0,
                0,
            );
        } else if (selectedRadioButton === 'radiobutton-last3') {
            const currentDate = new Date();
            currentDate.setMonth(currentDate.getMonth() - 2);
            existingData = toUnixTime(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                currentDate.getDate(),
                0,
                0,
                0,
            );
        }
        return existingData;
    };

    const handleStoreDataConnector = () => {
        const existingData = getExistingDataTimestamp();
        let dataConnectorPayload;

        switch (pageState.selectedConnector) {
            case ConnectionSystem.Zendesk: {
                dataConnectorPayload = {
                    system: ConnectionSystem.Zendesk,
                    initAt: existingData,
                    ticketData: true,
                    auditData: pageState.formData.auditData,
                    eventData: pageState.formData.eventData,
                    totalTimeSpentCustomField: '',
                    totalTimeLastUpdatedField: '',
                } as StoreDataConnectorConfigInput;
                break;
            }
            case ConnectionSystem.Gorgias: {
                dataConnectorPayload = {
                    system: ConnectionSystem.Gorgias,
                    initAt: existingData,
                };
                break;
            }
            default:
                dataConnectorPayload = undefined;
        }

        if (dataConnectorPayload) {
            createConnection({
                variables: {
                    createConnectionInput: {
                        organizationId: instanceId,
                        email: connectionData.username,
                        system: connectionData.system,
                        subdomain: connectionData.subdomain,
                        token: connectionData.token,
                        configuration: {
                            auditData: dataConnectorPayload.auditData,
                            ticketData: dataConnectorPayload.ticketData,
                            eventData: dataConnectorPayload.eventData,
                        },
                        initAt: `${dataConnectorPayload.initAt}`,
                        totalTimeLastUpdatedField:
                            dataConnectorPayload.totalTimeLastUpdatedField,
                        totalTimeSpentCustomField:
                            dataConnectorPayload.totalTimeSpentCustomField,
                    },
                },
            });
        }
        setStoringConnectorConfig(true);
    };

    const handleContinue = () => {
        handleStoreDataConnector();
    };

    if (error) {
        return (
            <ErrorStep
                onDismiss={() => {
                    reset();
                    onBack();
                }}
            />
        );
    }

    const isZendesk = pageState.selectedConnector.toLowerCase() === 'zendesk';

    return (
        <Container
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <StyledDivConfig>
                <StyledDivDescription>
                    <Typography
                        variant="h6"
                        style={{
                            color: colorTypographyGray,
                        }}
                    >
                        {t('Forms.Connections.Step3.Title')}
                    </Typography>

                    <Typography
                        variant="body1"
                        style={{
                            color: colorTypographyGray,
                            maxWidth: '407px',
                        }}
                    >
                        {t('Forms.Connections.Step3.Description')}
                    </Typography>
                    <StyledLink
                        to={
                            'https://www.partnerhero.com/software-terms-and-conditions'
                        }
                        color="primary"
                        target="_blank"
                    >
                        {t('Forms.Connections.Step3.LinkDescription')}
                    </StyledLink>
                </StyledDivDescription>

                <StyledDivInputs>
                    <Step3ToggleRadioGroup
                        radioList={radioList}
                        selected={selectedRadioButton}
                        onChange={setSelectedRadioButton}
                    />
                </StyledDivInputs>
            </StyledDivConfig>
            <div>
                {operatorConnectionEnableHelp && isZendesk && (
                    <>
                        <StyledDivConfig>
                            <ItemWithSwitch
                                description={t(
                                    'Forms.Connections.Settings.TalkDescription',
                                )}
                                header={`${upperFirst(
                                    pageState.selectedConnector || '',
                                )} ${t('Forms.Connections.Settings.Help')}`}
                                labelSwitch={`${upperFirst(
                                    pageState.selectedConnector || '',
                                )} ${t('Forms.Connections.Settings.Help')}`}
                                width="407px"
                                link={labelZendeskLink}
                                linkDescription={
                                    t(
                                        'Forms.Connections.Step3.LabelLinkDescription',
                                    ) as string
                                }
                            />
                        </StyledDivConfig>
                        <Divider
                            orientation="horizontal"
                            sx={{
                                width: '100%',
                            }}
                        />
                    </>
                )}
                {operatorConnectionEnableTalk && isZendesk && (
                    <>
                        <StyledDivConfig>
                            <ItemWithSwitch
                                description={t(
                                    'Forms.Connections.Settings.HelpDescription',
                                )}
                                header={`${upperFirst(
                                    pageState.selectedConnector || '',
                                )} ${t('Forms.Connections.Settings.Talk')}`}
                                labelSwitch={`${upperFirst(
                                    pageState.selectedConnector || '',
                                )} ${t('Forms.Connections.Settings.Talk')}`}
                                width="407px"
                            />
                        </StyledDivConfig>
                        <Divider
                            orientation="horizontal"
                            sx={{
                                width: '100%',
                            }}
                        />
                    </>
                )}
            </div>
            <StyledDivButtons
                style={{
                    marginTop: '64px',
                    marginBottom: '-18px',
                }}
            >
                <Button
                    variant="text"
                    onClick={onCancel}
                    size="small"
                    disabled={storingConnectorConfig}
                >
                    {t('Forms.Connections.Step3.CancelButton')}
                </Button>
                <Button
                    disabled={storingConnectorConfig}
                    sx={{
                        backgroundColor: '#E0E0E0',
                        color: 'rgba(0, 0, 0, 0.87)',
                        '&:hover': {
                            backgroundColor: '#E0E0E0',
                            color: 'rgba(0, 0, 0, 0.87)',
                        },
                    }}
                    variant="contained"
                    size="small"
                    onClick={onBack}
                    startIcon={<ArrowBack />}
                >
                    {t('Forms.Connections.Step3.BackButtonButton')}
                </Button>
                <Button
                    disabled={storingConnectorConfig}
                    variant={storingConnectorConfig ? 'text' : 'contained'}
                    color="success"
                    size="small"
                    onClick={handleContinue}
                    sx={{ minWidth: '80px' }}
                >
                    {storingConnectorConfig
                        ? t('Forms.Connections.Step3.LoadingButton', {
                              selectedConnector: pageState.selectedConnector,
                          })
                        : t('Forms.Connections.Step3.FinishButton')}
                    {storingConnectorConfig && (
                        <CircularProgress
                            variant="indeterminate"
                            size="0.75rem"
                            sx={{
                                marginLeft: '8px',
                            }}
                        />
                    )}
                </Button>
            </StyledDivButtons>
        </Container>
    );
}

export default FormSelectData;
